/** Step 1: the ticket's own fields and what it grants. Creates the ticket when new. */
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { Input } from "../../../../components/inputs/Input";
import PriceInput from "../../../../components/inputs/PriceInput";
import CustomSelect from "../../../../components/customSelect";
import { TertiaryButton } from "../../../../components/Button";
import { POSToken, TicketingItem } from "../../../../types/tickets";
import { CREATE_TICKETING_ITEM, UPDATE_TICKETING_ITEM } from "../../ticket-items/mutations";
import { ensureTokens, ensureTokensBulk, recordRename } from "../../../tokens/registry";
import { parseTicketModifiers, permutationTokenNames } from "../../../tokens/variantName";
import type { StepProps } from "../TicketingSetupPage";
import { ticketHasSales, ticketPath } from "../types";
import { buildTokenPayload, EGRESS_SUFFIX } from "../tokenPayload";
import { Card, CheckRow, Chip, StepFooter } from "../ui";

const client = createApolloClient();
const { getState: getUserState } = userStore;

type Draft = {
  name: string;
  price: number;
  taxPercent: string;
  category: string;
  salesLimit: string;
  orderQuantityLimit: string;
  availablePublicSale: boolean;
  isActive: boolean;
  isTopUp: boolean;
  unlimitedIngress: boolean;
  includeEgress: boolean;
  includePosTokens: boolean;
  posTokens: POSToken[];
  includeCash: boolean;
  cashBalance: number;
  includePromo: boolean;
  promoBalance: number;
};

const emptyDraft = (category: string): Draft => ({
  name: "", price: 0, taxPercent: "", category, salesLimit: "", orderQuantityLimit: "",
  availablePublicSale: true, isActive: true, isTopUp: false, unlimitedIngress: false, includeEgress: false,
  includePosTokens: false, posTokens: [{ name: "", quantity: 0 }], includeCash: false, cashBalance: 0, includePromo: false, promoBalance: 0,
});

const draftFrom = (t: TicketingItem): Draft => ({
  name: t.name ?? "",
  price: (Number(t.price) || 0) / 100,
  taxPercent: String(Number(((Number(t.tax_percentage) || 0) * 100).toFixed(4))),
  category: t.category ?? "",
  salesLimit: t.sales_limit_balance != null ? String(t.sales_limit_balance) : "",
  orderQuantityLimit: t.order_quantity_limit != null ? String(t.order_quantity_limit) : "",
  availablePublicSale: !!t.available_public_sale,
  isActive: !!t.is_active,
  isTopUp: !!t.cashless_top_up_item,
  unlimitedIngress: !!t.unlimited_ingress,
  includeEgress: !!t.include_egress,
  includePosTokens: !!t.include_pos_tokens,
  posTokens: (t.pos_token_payload?.length ? t.pos_token_payload : [{ name: "", quantity: 0 }]).map((p) => ({ name: p?.name ?? "", quantity: Number(p?.quantity) || 0 })),
  includeCash: (Number(t.included_cash_balance) || 0) > 0,
  cashBalance: (Number(t.included_cash_balance) || 0) / 100,
  includePromo: (Number(t.included_promo_balance) || 0) > 0,
  promoBalance: (Number(t.included_promo_balance) || 0) / 100,
});

export const DetailsStep = ({ data, refresh, ticket, ticketId, checks }: StepProps) => {
  const event = data.event!;
  const navigate = useNavigate();
  const categoryOptions = useMemo(() => data.categories.map((c) => ({ label: c.reference_label, value: c.reference_value })), [data.categories]);
  const defaultCategory = categoryOptions.find((c) => c.value === "Ticket")?.value ?? categoryOptions[0]?.value ?? "Ticket";
  const [draft, setDraft] = useState<Draft>(ticket ? draftFrom(ticket) : emptyDraft(defaultCategory));
  const [saving, setSaving] = useState(false);
  const hasSales = ticket ? ticketHasSales(ticket) : false;
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (): Promise<number> => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Ticket name is required");
      throw new Error("validation");
    }
    if (ticket && hasSales && ticket.name !== name) {
      const ok = window.confirm(`"${ticket.name}" has sales. Renaming it changes the scan name on new tickets; the old name is kept as an alias so sold tickets still scan. Continue?`);
      if (!ok) throw new Error("cancelled");
    }
    const tokenPayload = buildTokenPayload({ name, isTopUp: draft.isTopUp, unlimitedIngress: draft.unlimitedIngress, includeEgress: draft.includeEgress, includePosTokens: draft.includePosTokens, posTokens: draft.posTokens });
    const posTokens = draft.includePosTokens ? draft.posTokens.filter((p) => p.name.trim()) : [{ name: "", quantity: 0 }];
    const limit = parseInt(draft.salesLimit, 10);
    const orderLimit = parseInt(draft.orderQuantityLimit, 10);
    const common = {
      name,
      is_active: draft.isActive,
      event_id: event.id,
      category: draft.category || defaultCategory,
      price: Math.round((Number(draft.price) || 0) * 100),
      tax_percentage: ((Number(draft.taxPercent) || 0) / 100).toFixed(8),
      last_updated_by: getUserState().user?.id,
      token_payload: tokenPayload,
      unlimited_ingress: draft.unlimitedIngress,
      include_egress: draft.includeEgress,
      pos_token_payload: posTokens,
      include_pos_tokens: draft.includePosTokens,
      order_quantity_limit: Number.isFinite(orderLimit) ? orderLimit : null,
      available_public_sale: draft.availablePublicSale,
      cashless_top_up_item: draft.isTopUp,
      included_cash_balance: draft.includeCash ? Math.round((Number(draft.cashBalance) || 0) * 100) : 0,
      included_promo_balance: draft.includePromo ? Math.round((Number(draft.promoBalance) || 0) * 100) : 0,
    };
    setSaving(true);
    try {
      let id: number;
      if (ticket) {
        await client.mutate({ mutation: UPDATE_TICKETING_ITEM, variables: { input: { ...common, sales_limit_balance: Number.isFinite(limit) ? limit : null }, where: { id: { _eq: ticket.id } } } });
        if (!draft.isTopUp) await recordRename(client, event.id, ticket.name, name);
        if (draft.includeEgress) await recordRename(client, event.id, ticket.name ? ticket.name + EGRESS_SUFFIX : "", name + EGRESS_SUFFIX);
        if (draft.includePosTokens) await ensureTokens(client, event.id, posTokens.map((p) => p.name));
        const groups = parseTicketModifiers(ticket.modifiers);
        if (groups.length) await ensureTokensBulk(client, event.id, permutationTokenNames(Object.keys(tokenPayload), groups));
        id = ticket.id;
        toast.success("Ticket saved");
      } else {
        const res = await client.mutate({
          mutation: CREATE_TICKETING_ITEM,
          variables: { input: { ...common, description: "", additional_email_description: "", sales_limit: Number.isFinite(limit) ? limit : null, sales_limit_balance: Number.isFinite(limit) ? limit : null, sort_order: 0 } },
        });
        id = Number(res.data?.insert_items_ticketing_one?.id);
        await ensureTokens(client, event.id, Object.keys(tokenPayload));
        toast.success("Ticket created");
      }
      await client.clearStore();
      refresh();
      return id;
    } catch (err) {
      toast.error("Failed to save ticket: " + err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const stepChecks = checks.filter((c) => c.step === "details");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
      <div className="flex flex-col gap-3">
        <Card title="Details" subtitle="The ticket as the buyer sees it, and what it grants once scanned.">
          {hasSales && <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-3 py-2"><Chip tone="blue">Has sales</Chip> Renaming changes the scan name for new tickets. Sold tickets keep working through an alias.</div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2"><Input label="Name" name="setup_ticket_name" value={draft.name} onChange={(v) => set({ name: String(v ?? "") })} required /></div>
            <CustomSelect title="Category" name="setup_ticket_category" options={categoryOptions} value={draft.category} onChange={(v) => set({ category: String(v ?? "") })} />
            <PriceInput label="Price" name="setup_ticket_price" value={draft.price} onChange={(v) => set({ price: v ?? 0 })} />
            <Input label="Tax % ( 7.5 = 7.5% )" name="setup_ticket_tax" type="number" step="any" min={0} value={draft.taxPercent} onChange={(v) => set({ taxPercent: String(v ?? "") })} />
            <div />
            <Input label={ticket ? "Remaining available" : "Total available (blank = unlimited)"} name="setup_ticket_limit" type="number" value={draft.salesLimit} onChange={(v) => set({ salesLimit: String(v ?? "") })} />
            <Input label="Max per order" name="setup_ticket_order_limit" type="number" value={draft.orderQuantityLimit} onChange={(v) => set({ orderQuantityLimit: String(v ?? "") })} />
            <div className="flex flex-col gap-2 justify-end text-sm text-gray-700">
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.availablePublicSale} onChange={(e) => set({ availablePublicSale: e.target.checked })} /> Sell on the public page</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.isActive} onChange={(e) => set({ isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3">
            <div className="text-sm font-semibold text-gray-900 mb-2">What this ticket grants</div>
            <div className="flex flex-col gap-2 text-sm text-gray-700">
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.isTopUp} onChange={(e) => set({ isTopUp: e.target.checked })} /> This is a wallet top-up, not an entry ticket</label>
              {!draft.isTopUp && (
                <>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!draft.unlimitedIngress} onChange={(e) => set({ unlimitedIngress: !e.target.checked })} /> One entry scan <span className="text-xs text-gray-500">(untick for unlimited re-entry)</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={draft.includeEgress} onChange={(e) => set({ includeEgress: e.target.checked })} /> Exit scans</label>
                </>
              )}
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.includePosTokens} onChange={(e) => set({ includePosTokens: e.target.checked })} /> POS tokens (beer, food)</label>
              {draft.includePosTokens && (
                <div className="ml-6 flex flex-col gap-2 max-w-md">
                  {draft.posTokens.map((p, i) => (
                    <div key={i} className="grid grid-cols-[minmax(0,1fr)_90px_24px] gap-2 items-end">
                      <Input label={i === 0 ? "Token name" : ""} name={`setup_pos_name_${i}`} value={p.name} onChange={(v) => set({ posTokens: draft.posTokens.map((x, j) => (j === i ? { ...x, name: String(v ?? "") } : x)) })} />
                      <Input label={i === 0 ? "Qty" : ""} name={`setup_pos_qty_${i}`} type="number" value={p.quantity} onChange={(v) => set({ posTokens: draft.posTokens.map((x, j) => (j === i ? { ...x, quantity: Number(v) || 0 } : x)) })} />
                      <button type="button" className="text-gray-400 hover:text-red-600 mb-2" aria-label="Remove token" onClick={() => set({ posTokens: draft.posTokens.filter((_, j) => j !== i) })}><XCircleIcon className="h-5 w-5" /></button>
                    </div>
                  ))}
                  <TertiaryButton type="button" onClick={() => set({ posTokens: [...draft.posTokens, { name: "", quantity: 0 }] })}>+ Add token</TertiaryButton>
                </div>
              )}
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.includeCash} onChange={(e) => set({ includeCash: e.target.checked })} /> Cash balance on the wristband</label>
              {draft.includeCash && <div className="ml-6 w-40"><PriceInput label="Cash amount" name="setup_cash" value={draft.cashBalance} onChange={(v) => set({ cashBalance: v ?? 0 })} /></div>}
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.includePromo} onChange={(e) => set({ includePromo: e.target.checked })} /> Promo balance on the wristband</label>
              {draft.includePromo && <div className="ml-6 w-40"><PriceInput label="Promo amount" name="setup_promo" value={draft.promoBalance} onChange={(v) => set({ promoBalance: v ?? 0 })} /></div>}
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Description and the confirmation email text stay on the full form.
            {ticket && <> <Link to={`/ticketing-items/${ticket.id}/edit`}>Open full form</Link></>}
          </div>
        </Card>
        <StepFooter
          ticketId={ticketId}
          back="overview"
          nextLabel="Save and continue to Modifiers"
          busy={saving}
          onSave={async () => {
            await save();
          }}
          onNext={async () => {
            const id = await save();
            navigate(ticketPath(id, "modifiers"));
          }}
        />
      </div>
      {ticket && (
        <Card title="Checks for this step">
          {stepChecks.length === 0 ? <div className="text-sm text-gray-500">Nothing to report.</div> : stepChecks.map((c, i) => <CheckRow key={i} check={c} ticketId={ticketId} />)}
        </Card>
      )}
    </div>
  );
};
